import { IDocumentCategory } from '../../types/order/documents/IDocumentCategoryType';

// JET-272 provides this order of document phases
const documentPhases = ['prepared', 'preliminary', 'unexecuted', 'executed', 'final', 'post-closing', 'recorded'];

type NameAnalysis = {
  primaryName: string;
  phase: string;
};

type NumberAnalysis = {
  primaryName: string;
  number: number;
};

const analyzeCategoryName = (categoryName: string): NameAnalysis => {
  const nameArr = categoryName.split('-');
  const phaseWordIndex = categoryName.endsWith('post-closing') ? -2 : -1;
  return { primaryName: nameArr.slice(0, phaseWordIndex).join('-'), phase: nameArr.slice(phaseWordIndex).join('-') };
};

const analyzeNumberedName = (categoryName: string): NumberAnalysis => {
  const nameArr = categoryName.split('-');
  const number = Number(nameArr.at(-1));

  return { primaryName: nameArr.slice(0, -1).join('-'), number };
};

const phaseCompare = (phaseA: string, phaseB: string): number =>
  documentPhases.indexOf(phaseA) - documentPhases.indexOf(phaseB);

const hasDocumentPhase = (name: string): boolean => documentPhases.some((phase) => name.endsWith(phase));

// we require a hyphen so that names like w9 don't produce a false positive
const endsWithHyphenNumber = (name: string): boolean => /-\d+$/.test(name);

// called only when both names end with hyphenated numbers (e.g. `payoff-courtesy-payoff-payoff-1` or `payoff-courtesy-payoff-payoff-10`)
const numberedComparator = (categoryNameA: string, categoryNameB: string): number => {
  const [categoryADecomposition, categoryBDecomposition] = [categoryNameA, categoryNameB].map(analyzeNumberedName);
  if (categoryADecomposition.primaryName !== categoryBDecomposition.primaryName) {
    return categoryNameA.localeCompare(categoryNameB);
  }

  return categoryADecomposition.number - categoryBDecomposition.number;
};

// JET-272 comparator. intended for use only by the category bootstrap component to load into Redux.
export const categorySortingComparator = (categoryA: IDocumentCategory, categoryB: IDocumentCategory): number => {
  if (categoryA.isLegacy || categoryB.isLegacy) {
    return categoryA.name.localeCompare(categoryB.name);
  }

  if (endsWithHyphenNumber(categoryA.name) && endsWithHyphenNumber(categoryB.name)) {
    return numberedComparator(categoryA.name, categoryB.name);
  }

  if (!hasDocumentPhase(categoryA.name)) {
    return categoryA.name.localeCompare(categoryB.name);
  }
  if (!hasDocumentPhase(categoryB.name)) {
    return categoryA.name.localeCompare(categoryB.name);
  }

  // we've determined that both categoryA and categoryB have a phase
  const [categoryADecomposition, categoryBDecomposition] = [categoryA.name, categoryB.name].map(analyzeCategoryName);
  if (categoryADecomposition.primaryName !== categoryBDecomposition.primaryName) {
    return categoryA.name.localeCompare(categoryB.name);
  }
  return phaseCompare(categoryADecomposition.phase, categoryBDecomposition.phase);
};

export const categorySortingHelperMethods = {
  hasDocumentPhase,
  phaseCompare,
  analyzeCategoryName,
  analyzeNumberedName,
  endsWithHyphenNumber,
  numberedComparator,
};
